import { LocationItem, SearchData } from '../../../types/search';
import { SearchService } from '../services/searchService';
import { UrlService } from '../services/urlService';
import { DEFAULT_SEARCH_VALUES, LOCATION_TYPE_MAPPING } from '../constants/searchConstants';
import { highlightText } from '../utils';
import { LocationSegment } from '../../../types/locationSegment';
import { createSegmentFromItem, segmentsToDisplayString } from '../utils/segmentHelpers';

interface CategoryItem {
  category_ID: string;
  category_Name_TH: string;
  category_Name_EN: string;
  category_Name_CN: string;
}

interface ActionItem {
  id: string;
  action: string;
}

interface SearchHandlersProps {
  setSearchText: (text: string) => void;
  setTransactionType: (type: string) => void;
  setPropertyType: (type: string) => void;
  setSelectedLocation: (location: LocationItem | null) => void;
  setSelectedSubLocation: (location: LocationItem | null) => void;
  setIsSearchDisabled: (disabled: boolean) => void;
  setShowDropdown: (show: boolean) => void;
  setShowLocationDropdown: (show: boolean) => void;
  setModalSearchValue: (value: string) => void;
  setIsSearching: (searching: boolean) => void;
  setSearchResults: (results: LocationItem[]) => void;
  setHasMore: (hasMore: boolean) => void;
  setPage: (page: number) => void;
  setIsLoadingMore: (loading: boolean) => void;
  setOpenModal: (open: boolean) => void;
  setLocationTypeId: (id: number | null) => void;
  setLocationData: (data: LocationItem[]) => void;
  setLoadingLocationData: (loading: boolean) => void;
  setLocationSearchText: (text: string) => void;
  setNavigationHistory: (history: LocationItem[]) => void;
  setModalView: (view: 'main' | 'sub') => void;
  setSelectedMainOption: (option: LocationItem | null) => void;

  searchText: string;
  transactionType: string;
  propertyType: string;
  priceMin: number;
  priceMax: number;
  selectedLocation: LocationItem | null;
  selectedSubLocation: LocationItem | null;
  searchResults: LocationItem[];
  page: number;
  hasMore: boolean;
  isSearching: boolean;
  isLoadingMore: boolean;
  locationTypeId: number | null;
  locationData: LocationItem[];
  modalSearchValue: string;
  selectedMainOption: LocationItem | null;
  navigationHistory: LocationItem[];
  locationSegments: LocationSegment[];
  pushSegment: (seg: LocationSegment) => void;
  popSegment: () => void;
  replaceLastSegmentLabel: (label: string, locale: string) => void;
  setLocationSegments: (segs: LocationSegment[]) => void;
  
  selectedBedrooms?: string | null;
  selectedBathrooms?: string | null;
  selectedQuota?: string | null;
  selectedStatus?: string | null;
  selectedFloor?: string | null;
  selectedView?: string | null;
  selectedDirection?: string | null;
  selectedPublishedBy?: string | null;
  selectedPostedDate?: string | null;
  areaMin?: number;
  areaMax?: number;

  selectedDynamicFilters?: Record<number, number>;
  
  getSelectedQueryParams?: () => Record<string, any>;

  filteredActions: ActionItem[];
  actionMapping: Record<string, string>;
  filteredCategories: CategoryItem[];
  categoryMapping: Record<string, string>;

  locale: string;
  t: (key: string) => string;
  searchDebounceTimeout: React.MutableRefObject<NodeJS.Timeout | null>;
}

export const createSearchHandlers = (props: SearchHandlersProps) => {
  const handleTransactionTypeChange = (value: string) => {
    props.setTransactionType(value);
  };

  const handlePropertyTypeChange = (value: string) => {
    props.setPropertyType(value);
  };

  const getActionIdFromType = (type: string): number | null => {
    return SearchService.getActionIdFromType(type, props.filteredActions, props.actionMapping);
  };

  const getCategoryIdFromType = (type: string): number | null => {
    return SearchService.getCategoryIdFromType(type, props.t, props.filteredCategories, props.categoryMapping, props.locale);
  };

  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    // ensure there is at least one segment to edit
    if (props.locationSegments.length === 0) {
      props.pushSegment({
        id: 'temp',
        label: { th: searchValue, en: searchValue, cn: searchValue },
        slug: ''
      });
    } else {
      props.replaceLastSegmentLabel(searchValue, props.locale);
    }

    props.setSearchText(searchValue); // keep legacy state in sync

    if (props.selectedLocation || props.selectedSubLocation) {
      const currentSelectedText = props.selectedLocation && props.selectedSubLocation ? 
        `${props.selectedLocation.label} - ${props.selectedSubLocation.label}` : 
        props.selectedLocation?.label || props.selectedSubLocation?.label || '';
      
      if (searchValue !== currentSelectedText && 
          !currentSelectedText.includes(searchValue)) {
        props.setSelectedLocation(null);
        props.setSelectedSubLocation(null);
      }
    }

    if (!searchValue || searchValue.trim() === '') {
      props.setSearchResults([]);
      props.setShowDropdown(false);
      props.setLocationSegments([]);
      return;
    }

    props.setShowDropdown(true);
    props.setShowLocationDropdown(false);
    props.setIsSearching(true);

    if (props.searchDebounceTimeout.current) {
      clearTimeout(props.searchDebounceTimeout.current);
    }

    props.searchDebounceTimeout.current = setTimeout(async () => {
      if (props.selectedLocation || props.selectedSubLocation) {
        const currentSelectedText = props.selectedLocation && props.selectedSubLocation ? 
          `${props.selectedLocation.label} - ${props.selectedSubLocation.label}` : 
          props.selectedLocation?.label || props.selectedSubLocation?.label || '';
        
        if (searchValue === currentSelectedText) {
          const dropdownItems: LocationItem[] = [];
          
          if (props.selectedSubLocation) {
            dropdownItems.push({
              ...props.selectedSubLocation,
              label: searchValue, // แสดงเป็น format เต็ม
              parentName: props.selectedLocation?.label || ''
            });
          } else if (props.selectedLocation) {
            dropdownItems.push({
              ...props.selectedLocation,
              label: searchValue
            });
          }
          
          props.setSearchResults(dropdownItems);
          props.setIsSearching(false);
          return;
        }
      }

      try {
        const result = await SearchService.performUniversalSearch(searchValue, props.locale, 1, 10);

        props.setSearchResults(result.results);
        props.setHasMore(result.total > result.results.length);

        props.setPage(1);
      } catch (error) {
        props.setSearchResults([]);
      } finally {
        props.setIsSearching(false);
      }
    }, DEFAULT_SEARCH_VALUES.DEBOUNCE_DELAY);
  };

  const handleClearSearch = () => {
    console.log('SearchHandlers: handleClearSearch triggered');
    props.setSearchText('');
    props.setLocationSegments([]);
    props.setSelectedLocation(null);
    props.setSelectedSubLocation(null);
    props.setIsSearchDisabled(false);
    props.setShowDropdown(false);
    props.setShowLocationDropdown(false);
    props.setModalSearchValue('');
  };

  const handleDropdownItemClick = (item: LocationItem) => {
    props.setShowDropdown(false);

    const getLocalizedName = (item: any) => {
      if (item.nameTH || item.nameEN || item.nameCN) {
        return props.locale === 'th' ? (item.nameTH || item.label) : 
               props.locale === 'en' ? (item.nameEN || item.label) : 
               (item.nameCN || item.label);
      }
      return item.label;
    };

    const getLocalizedParentName = (item: any) => {
      if (item.parentNameTH || item.parentNameEN || item.parentNameCN) {
        return props.locale === 'th' ? (item.parentNameTH || item.parentName) : 
               props.locale === 'en' ? (item.parentNameEN || item.parentName) : 
               (item.parentNameCN || item.parentName);
      }
      return item.parentName;
    };

    const localizedParentName = getLocalizedParentName(item);
    const localizedName = getLocalizedName(item);

    if (localizedParentName) {
      if (localizedParentName.includes(' > ')) {
        const [provinceName, amphurName] = localizedParentName.split(' > ');
        const districtName = localizedName;
        
        const amphurLocation: LocationItem = {
          value: `amphur_${amphurName}`,
          label: amphurName,
          typeId: 0,
          isAmphur: true,
          parentName: provinceName
        };
        
        const districtLocation: LocationItem = {
          value: item.value || `district_${districtName}`,
          label: districtName,
          originalLabel: districtName,
          typeId: 0,
          isDistrict: true,
          parentName: localizedParentName
        };
        
        props.setSelectedLocation(amphurLocation);
        props.setSelectedSubLocation(districtLocation);
        props.setSearchText(`${provinceName} - ${amphurName} - ${districtName}`);
        
      } else {
        const provinceName = localizedParentName;
        const amphurName = localizedName;
        
        const provinceLocation: LocationItem = {
          value: `province_${provinceName}`,
          label: provinceName,
          typeId: 0
        };
        
        const amphurLocation: LocationItem = {
          value: item.value || `amphur_${amphurName}`,
          label: amphurName,
          originalLabel: amphurName,
          typeId: 0,
          isAmphur: true,
          parentName: provinceName
        };
        
        props.setSelectedLocation(provinceLocation);
        props.setSelectedSubLocation(amphurLocation);
        props.setSearchText(`${provinceName} - ${amphurName}`);
      }
    } else {
      const provinceName = localizedName;
      
      const provinceLocation: LocationItem = {
        value: item.value || `province_${provinceName}`,
        label: provinceName,
        originalLabel: provinceName,
        typeId: 0
      };
      
      props.setSelectedLocation(provinceLocation);
      props.setSelectedSubLocation(null);
      props.setSearchText(provinceName);
    }

    if (item.desc && !localizedParentName) {
      const parentTypeId = item.typeId;
      const parentDesc = item.desc;

      const parentLocation: LocationItem = {
        value: 'parent_' + item.value,
        label: parentDesc,
        typeId: parentTypeId
      };

      props.setSelectedLocation(parentLocation);
      props.setSelectedSubLocation(item);
      props.setSearchText(`${parentDesc} - ${item.originalLabel || localizedName}`);
    }

    // Build segment(s) from clicked item and replace segments appropriately
    const seg = createSegmentFromItem(item, props.locale);
    if (item.typeId === 0 && !item.isAmphur && !item.isDistrict) {
      // province level – clear then push
      props.setLocationSegments([seg]);
    } else if (item.isAmphur) {
      // amphur – ensure province first
      if (props.locationSegments.length < 1) props.pushSegment({ id: 'unknown', label: seg.label, slug: seg.slug });
      props.setLocationSegments([props.locationSegments[0], seg]);
    } else if (item.isDistrict) {
      // district – ensure province & amphur length 2
      if (props.locationSegments.length < 2) {
        // cannot infer; push anyway
        props.pushSegment(seg);
      } else {
        props.pushSegment(seg);
      }
    }
  };

  const handleSearchInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (props.searchText && props.searchText.length > 0) {
      props.setShowDropdown(true);
      props.setShowLocationDropdown(false);

      if (props.searchResults.length === 0) {
        handleSearchTextChange({ target: { value: props.searchText } } as React.ChangeEvent<HTMLInputElement>);
      }
    } else {
      props.setShowDropdown(false);
      props.setShowLocationDropdown(true);
    }
  };

  const handleLocationTypeClick = (locationType: string) => {
    props.setShowLocationDropdown(false); // ปิด LocationDropdown

    props.setLoadingLocationData(true);
    props.setModalSearchValue(''); // รีเซ็ตค่าค้นหาเมื่อเปลี่ยนหมวดหมู่

    props.setLocationSearchText(locationType);

    props.setModalView('main');
    props.setSelectedMainOption(null);

    props.setPage(1);
    props.setHasMore(true);

    let selectedLocationTypeId: number | null = null;

    if (locationType === props.t('location_categories.province')) {
      selectedLocationTypeId = 0; // จังหวัด
    } else if (locationType === props.t('location_categories.project_name')) {
      selectedLocationTypeId = -1; // โครงการ
    } else if (locationType === props.t('location_categories.mass_transit')) {
      selectedLocationTypeId = 10; // รถไฟฟ้า
    } else if (locationType === props.t('location_categories.shopping_mall')) {
      selectedLocationTypeId = 11; // ห้างสรรพสินค้า
    } else if (locationType === props.t('location_categories.university')) {
      selectedLocationTypeId = 17; // มหาวิทยาลัย
    } else if (locationType === props.t('location_categories.road')) {
      selectedLocationTypeId = 20; // ถนน
    } else if (locationType === props.t('location_categories.hospital')) {
      selectedLocationTypeId = 9; // โรงพยาบาล
    } else if (locationType === props.t('location_categories.park')) {
      selectedLocationTypeId = 8; // สวนสาธารณะ
    } else if (locationType === props.t('location_categories.airport')) {
      selectedLocationTypeId = 1; // สนามบิน
    } else if (locationType === props.t('location_categories.popular_location')) {
      selectedLocationTypeId = 2; // สถานที่ยอดนิยม
    }

    props.setLocationTypeId(selectedLocationTypeId);
    
    props.setOpenModal(true);
    
    props.setLocationData([]);
    props.setNavigationHistory([]);

    if (selectedLocationTypeId !== null && selectedLocationTypeId !== undefined) {
      loadLocationDataBySearch(selectedLocationTypeId, '');
    }
  };

  const loadLocationDataBySearch = async (typeId: number, searchValue: string) => {
    props.setLoadingLocationData(true);
    props.setHasMore(true);
    
    try {
      const result = await SearchService.loadLocationDataBySearch(
        typeId, 
        searchValue, 
        props.transactionType, 
        props.propertyType, 
        props.locale,
        getActionIdFromType,
        getCategoryIdFromType
      );

      props.setLocationData(result.data);
      props.setHasMore(result.hasMore);
    } catch (error) {
      props.setLocationData([]);
      props.setHasMore(false);
    } finally {
      props.setLoadingLocationData(false);
    }
  };

  const handleSearch = () => {
    const apiQueryParams = props.getSelectedQueryParams ? props.getSelectedQueryParams() : {};
    
    const searchData: SearchData = {
      transactionType: props.transactionType,
      propertyType: props.propertyType,
      searchText: props.searchText,
      priceMin: props.priceMin,
      priceMax: props.priceMax,
      locationSegments: props.locationSegments,
      location: props.selectedLocation,
      subLocation: props.selectedSubLocation,
      selectedBedrooms: props.selectedBedrooms,
      selectedBathrooms: props.selectedBathrooms,
      selectedQuota: props.selectedQuota,
      selectedStatus: props.selectedStatus,
      selectedFloor: props.selectedFloor,
      selectedView: props.selectedView,
      selectedDirection: props.selectedDirection,
      selectedPublishedBy: props.selectedPublishedBy,
      selectedPostedDate: props.selectedPostedDate,
      areaMin: props.areaMin,
      areaMax: props.areaMax,
      selectedDynamicFilters: props.selectedDynamicFilters,
      apiQueryParams: apiQueryParams
    };

    const url = UrlService.generateSearchUrl(
      searchData,
      props.filteredActions,
      props.filteredCategories,
      props.locationTypeId,
      props.locale,
      props.t
    );

    UrlService.navigateToUrl(url);
  };

  const handleDropdownScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (props.isSearching || props.isLoadingMore || !props.hasMore) return;

    const target = event.target as HTMLDivElement;
    const scrollPercentage = (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;

    if (scrollPercentage > 80) {
      if (props.searchText.length > 0) {
        loadMoreSearchResults();
      }
    }
  };

  const loadMoreSearchResults = async () => {
    if (props.isSearching || props.isLoadingMore || !props.hasMore) return;

    props.setIsLoadingMore(true);

    try {
      const result = await SearchService.loadMoreSearchResults(
        props.searchText, 
        props.locale, 
        props.page, 
        props.searchResults
      );

      props.setSearchResults(result.results);
      props.setHasMore(result.hasMore);
      props.setPage(result.newPage);
    } catch (error) {
    } finally {
      props.setIsLoadingMore(false);
    }
  };

  const handleMobileLoadMore = async () => {
    if (props.isSearching || props.isLoadingMore || !props.hasMore) return;

    if (props.searchText.length > 0) {
      loadMoreSearchResults();
    }
  };

  return {
    handleTransactionTypeChange,
    handlePropertyTypeChange,
    handleSearchTextChange,
    handleClearSearch,
    handleDropdownItemClick,
    handleSearchInputClick,
    handleLocationTypeClick,
    handleSearch,
    handleDropdownScroll,
    handleMobileLoadMore,
    loadLocationDataBySearch,
    getActionIdFromType,
    getCategoryIdFromType
  };
}; 